<template>
  <span />
</template>
<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('AudioTracker.vue');

import Locator from '@shared/publication/locator.mjs';
import AudioTrackerEvents from '@/enums/AudioTrackerEvents';
import ProgressTrackingTask from '@/enums/ProgressTrackingTask';
import TrackerStateEnum from '@/enums/TrackerStateEnum';

import UserProgressFactory from '@/classes/factories/UserProgressFactory';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';

export default {
  props: {
    publicationType: String
  },
  data() {
    return {
      startBackgroundLocator: '',
      currentRange: null,
      needSavePositionOnUserStartAudio: false,
      trackerState: TrackerStateEnum.STOP
    };
  },
  computed: {
    bookId() {
      return this.$store.getters['PlaybackStore/getCurrentBookId'];
    },
    currentTime() {
      return this.$store.getters['PlaybackStore/getCurrentTime'];
    },
    currentParaId() {
      return this.$store.getters['PlaybackStore/getCurrentParaId'];
    },
    currentLocator() {
      return this.$store.getters['PublicationStore/getLocatorByTime'](
        this.bookId,
        this.currentParaId,
        this.currentTime
      );
    },
    currentTrackingTask() {
      return this.$store.getters['ProgressStore/getCurrentTrackingTask'];
    },
    isAudioPlaying() {
      return this.$store.state.PlaybackStore.isPlaying;
    },
    isUserPlayAudio() {
      return this.$store.getters['PlaybackStore/isUserPlayAudio'];
    },
    isBackground() {
      return (
        this.$store.getters['ContextStore/isWebBackground'] ||
        this.$store.getters['ContextStore/isAppInBackground']
      );
    }
  },
  watch: {
    isUserPlayAudio() {
      this.needSavePositionOnUserStartAudio = this.isUserPlayAudio;
    },
    isAudioPlaying() {
      if (!this.isAudioPlaying) {
        this.$store.commit('ProgressStore/setTrackingTask', {
          trackingTasks: [
            {
              type: ProgressTrackingTask.STOP_AUDIO_TRACKER,
              payload: {}
            },
            {
              type: ProgressTrackingTask.START_DELAYED_POSITION_TRACKER,
              payload: {
                startTrackingDelay:
                  AppConstantsUtil.START_POSITION_TRACKER_SHORT_DELAY
              }
            }
          ]
        });
        try {
          this.updateUserActivityTrack(this.currentRange);
        } catch (error) {
          this.$_audioTrackerErrorHandler(error);
        }
      }
    },
    currentTrackingTask(task) {
      if (!task?.type) {
        return;
      }
      switch (task.type) {
        case ProgressTrackingTask.START_AUDIO_TRACKER:
          {
            const { serializeLocator } = task.payload;
            this.onAudioStart(serializeLocator);
            this.$store.commit('ProgressStore/removeTrackingTask', task);
          }
          break;
        case ProgressTrackingTask.STOP_AUDIO_TRACKER:
          this.onAudioStop();
          this.$store.commit('ProgressStore/removeTrackingTask', task);
          break;
        default:
          break;
      }
    },
    async isBackground(isInBackground) {
      if (isInBackground) {
        this.startBackgroundLocator = this.currentLocator;
      } else {
        try {
          await this.applyReadRangesAfterBackground(
            this.startBackgroundLocator,
            this.currentLocator
          );
        } catch (error) {
          logger.error(
            `Get error on apple reading ranges after background error: ${error}`
          );
        }
      }
    },
    currentLocator(serializeLocator) {
      if (
        serializeLocator &&
        this.isAudioPlaying &&
        this.trackerState !== TrackerStateEnum.START
      ) {
        this.$store.commit('ProgressStore/setTrackingTask', {
          trackingTasks: [
            {
              type: ProgressTrackingTask.STOP_POSITION_TRACKER,
              payload: {}
            },
            {
              type: ProgressTrackingTask.START_AUDIO_TRACKER,
              payload: { serializeLocator }
            }
          ]
        });
      }

      if (serializeLocator) {
        this.$_highLightIfNeed(serializeLocator);
      }

      if (
        this.trackerState === TrackerStateEnum.START &&
        serializeLocator &&
        this.currentRange
      ) {
        this.updateActivityPosition(this.currentRange);
      }
    }
  },

  methods: {
    async applyReadRangesAfterBackground(
      startSerializeBackgroundLocator,
      endSerializeBackgroundLocator
    ) {
      if (!startSerializeBackgroundLocator || !endSerializeBackgroundLocator) {
        return;
      }
      const starBackgroundLocator = Locator.deserialize(
        startSerializeBackgroundLocator
      );
      const endBackgroundLocator = Locator.deserialize(
        endSerializeBackgroundLocator
      );

      const startParaNum =
        starBackgroundLocator?.startLocator?._paragraphNumber || -1;
      const endParaNum =
        endBackgroundLocator?.endLocator?._paragraphNumber || -1;

      if (
        startParaNum < 0 ||
        endParaNum < 0 ||
        endParaNum - startParaNum <= 0
      ) {
        return;
      }

      let hasUpdate = false;
      for (let i = startParaNum; i < endParaNum; i++) {
        const paragraphId = `para_${i}`;

        const currentParaAlignment = this.$store.getters[
          'PublicationStore/getParagraphAlignment'
        ](this.bookId, paragraphId);

        let applyReadRange;
        if (currentParaAlignment) {
          const locators = currentParaAlignment[1];
          const serializeFirstLocator = locators[0];
          const serializeLastLocator = locators[locators.length - 1];

          const firstLocator = Locator.deserialize(serializeFirstLocator);
          const lastLocator = Locator.deserialize(serializeLastLocator);

          const locator = new Locator.InTextRangeLocator(
            firstLocator.startLocator,
            lastLocator.endLocator
          );
          applyReadRange = UserProgressFactory.createProgressRange(
            locator.startLocator,
            locator.endLocator,
            'audio'
          );
        }

        const isLastAudioLocator = this.$store.getters[
          'PublicationStore/isLastAudioLocator'
        ](applyReadRange.range);
        const paraId = applyReadRange.range.startLocator.prefixedParagraphId;

        const tracker = { position: applyReadRange.range.endLocator };
        const wordsCount = isLastAudioLocator ? this.$_getWordCount(paraId) : 0;

        this.$store.commit('ProgressStore/updateUserActivityTracker', {
          publicationId: this.bookId,
          type: this.publicationType,
          tracker,
          newRange: applyReadRange,
          wordsCount
        });
        hasUpdate = true;
      }

      if (hasUpdate) {
        await this.$store.dispatch('ProgressStore/saveUserActivity', {
          publicationId: this.bookId,
          type: this.publicationType
        });
      }
    },
    updateActivityPosition(currentRange) {
      this.updateCurrentRange();
      this.setCurrentLocator(currentRange);
    },
    onAudioStop() {
      this.trackerState = TrackerStateEnum.STOP;

      try {
        this.updateUserActivityTrack(this.currentRange);
      } catch (err) {
        this.$_audioTrackerErrorHandler(err);
      }

      this.currentRange = null;
    },
    async onAudioStart(serializeLocator) {
      try {
        this.trackerState = TrackerStateEnum.START;

        if (!serializeLocator) {
          logger.warn(`"currentScrubberLocator" is ${serializeLocator}`);
          return;
        }
        const locator = Locator.deserialize(serializeLocator);
        this.currentRange = UserProgressFactory.createProgressRange(
          locator.startLocator,
          locator.endLocator,
          'audio'
        );
        if (this.needSavePositionOnUserStartAudio) {
          this.needSavePositionOnUserStartAudio = false;
          this.updateActivityPosition(this.currentRange);
          try {
            await this.$store.dispatch('ProgressStore/saveUserActivity', {
              publicationId: this.bookId,
              type: this.publicationType
            });
          } catch (error) {
            logger.error(`get error on update audio position error: ${error}`);
          }
        }
      } catch (error) {
        this.$_audioTrackerErrorHandler(error);
      }
    },
    updateUserActivityTrack(currentRange) {
      if (!currentRange) {
        return;
      }
      this.updateActivityPosition(currentRange);
      const paraId = currentRange.range.startLocator.prefixedParagraphId;
      const isRead = this.$store.getters['ProgressStore/isReadParagraph'](
        this.publicationType,
        this.bookId,
        paraId
      );
      if (isRead) {
        return;
      }
      const isLastAudioLocator = this.$store.getters[
        'PublicationStore/isLastAudioLocator'
      ](currentRange.range);

      const tracker = { position: currentRange.range.endLocator };
      const wordsCount = isLastAudioLocator ? this.$_getWordCount(paraId) : 0;

      this.$store.commit('ProgressStore/saveReadParaRange', {
        startRangeLocator: currentRange.range.startLocator,
        endRangeLocator: currentRange.range.endLocator
      });

      if (!wordsCount) {
        logger.warn(
          `Try to save reading range without words count bookId: ${this.bookId}`
        );
        return;
      }
      if (!isLastAudioLocator) {
        return;
      }
      this.$store.dispatch('ProgressStore/updateUserActivityTracker', {
        publicationId: this.bookId,
        type: this.publicationType,
        tracker,
        newRange: currentRange,
        wordsCount
      });
    },

    $_getWordCount(paraId) {
      const defaultVal = 0;
      try {
        let blockMeta = this.$store.getters['BookStore/getPublicationParaMeta'](
          paraId
        );
        let wordsCount = blockMeta?.words;
        if (!wordsCount) {
          wordsCount = parseInt(
            document
              .getElementById(paraId)
              .getAttribute(AppConstantsUtil.WORD_COUNT_ATTRIBUTE)
          );
        }
        if (isNaN(wordsCount)) {
          logger.warn(
            `words count is NaN from para ${paraId} set default value:${defaultVal}`
          );
          return defaultVal;
        }
        return wordsCount;
      } catch (error) {
        logger.warn(
          `Get error in AudioTracker on get word number from  para ${this.currentParaId} element set defaul value:${defaultVal} error:${error}`
        );
      }
      return defaultVal;
    },
    updateCurrentRange() {
      const audioPosition = this.getCurrentLocator();
      if (this.currentRange?.range?.endLocator) {
        this.currentRange.range.endLocator = audioPosition;
      }
    },
    $_audioTrackerErrorHandler(error) {
      logger.fatal(`Error in AudioTracker: ${error.stack}`);
      this.$emit('audioTrackerEvent', {
        type: AudioTrackerEvents.ERROR,
        data: { error }
      });
    },
    getCurrentLocator() {
      const locator = Locator.deserialize(this.currentLocator);
      const endLocator = locator.endLocator;
      return new Locator.InTextLocator(
        endLocator.prefixedParagraphId,
        endLocator.logicalCharOffset
      );
    },
    $_highLightIfNeed(serializeLocator) {
      try {
        const locator = Locator.deserialize(serializeLocator);
        const paraId = locator.startLocator.prefixedParagraphId;
        const isRead = this.$store.getters['ProgressStore/isReadParagraph'](
          this.publicationType,
          this.bookId,
          paraId
        );
        this.$store.dispatch('ProgressStore/applyParaHighlightIfNeed', {
          paraId,
          isRead
        });
        if (isRead) {
          return;
        }
        const wordOffset = [
          locator.startLocator.logicalCharOffset,
          locator.endLocator.logicalCharOffset
        ];
        this.$store.dispatch('ProgressStore/highlightColorWordsIfNeed', {
          paraId,
          wordOffset
        });
      } catch (error) {
        logger.error(`Get error on highlight audio locator error: ${error}`);
      }
    },
    setCurrentLocator(currentRange) {
      try {
        const wordRange = currentRange.range;
        const locator = wordRange.endLocator;

        this.$store.commit('ProgressStore/setAudioCurrentLocator', {
          bookId: this.bookId,
          type: this.publicationType,
          currentLocator: Locator.serialize(locator)
        });
      } catch (error) {
        logger.error(`Get error on set current audio locator error: ${error}`);
      }
    }
  }
};
</script>
